import type { UserProfile, UserRole } from '../types';

export const VALID_ROLES: UserRole[] = [
  'admin',
  'executive',
  'manager',
  'sales_rep',
  'accountant',
  'marketing',
];

export const ROLE_DEFAULT_DISCOUNT_LIMITS: Record<UserRole, number> = {
  admin: 100,
  executive: 100,
  manager: 20,
  sales_rep: 10,
  accountant: 10,
  marketing: 0,
};

export const ROLE_CAN_APPROVE_DISCOUNTS: Record<UserRole, boolean> = {
  admin: true,
  executive: true,
  manager: true,
  sales_rep: false,
  accountant: false,
  marketing: false,
};

export interface UserCreationPayload {
  name: string;
  email: string;
  password?: string;
  role: UserRole;
  max_discount_limit?: number;
}

export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string>;
}

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

/**
 * Validates a user creation or edit payload.
 */
export function validateUserCreationPayload(payload: UserCreationPayload, isEdit = false): ValidationResult {
  const errors: Record<string, string> = {};

  // 1. Full Name Validation
  const trimmedName = (payload.name || '').trim();
  if (!trimmedName) {
    errors.name = 'Full name is required.';
  } else if (trimmedName.length < 2) {
    errors.name = 'Full name must be at least 2 characters long.';
  }

  // 2. Email Validation
  const trimmedEmail = (payload.email || '').trim().toLowerCase();
  if (!trimmedEmail) {
    errors.email = 'Corporate email address is required.';
  } else if (!EMAIL_REGEX.test(trimmedEmail)) {
    errors.email = 'Please provide a valid corporate email address (e.g. name@saindustries.com).';
  }

  // 3. Password Validation (Only on creation or if explicitly provided)
  if (!isEdit || payload.password) {
    if (!payload.password || payload.password.length < 6) {
      errors.password = 'Password must be at least 6 characters long.';
    }
  }

  // 4. Role Validation
  if (!payload.role || !VALID_ROLES.includes(payload.role)) {
    errors.role = `Invalid role. Must be one of: ${VALID_ROLES.join(', ')}`;
  }

  // 5. Discount Limit Validation
  if (payload.max_discount_limit !== undefined) {
    if (isNaN(payload.max_discount_limit) || payload.max_discount_limit < 0 || payload.max_discount_limit > 100) {
      errors.max_discount_limit = 'Maximum discount limit must be between 0% and 100%.';
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

/**
 * Maps page/route IDs to permitted roles.
 */
export const PAGE_ROLE_PERMISSIONS: Record<string, UserRole[]> = {
  dashboard: ['admin', 'executive', 'manager', 'sales_rep', 'accountant'],
  quotations: ['admin', 'executive', 'manager', 'sales_rep', 'accountant'],
  parties: ['admin', 'executive', 'manager', 'sales_rep', 'marketing'],
  companies: ['admin', 'executive', 'manager', 'sales_rep', 'accountant'],
  products: ['admin', 'executive', 'manager'],
  pricelist: ['admin', 'executive', 'manager'],
  lattice: ['admin', 'executive', 'manager', 'sales_rep'],
  reports: ['admin', 'executive', 'manager', 'accountant'],
  import: ['admin', 'executive', 'manager'],
  stock: ['admin', 'executive', 'manager'],
  marketing: ['admin', 'executive', 'manager', 'marketing'],
  tracker: ['admin', 'executive', 'manager', 'sales_rep', 'accountant'],
  help: ['admin', 'executive', 'manager', 'sales_rep', 'accountant', 'marketing'],
  team: ['admin', 'executive', 'manager'],
  admin: ['admin', 'executive'],
};

/**
 * Check if a given user/role has access to a specific page or route.
 */
export function hasPageAccess(role?: string, pageId?: string): boolean {
  if (!role || !pageId) return false;
  const permittedRoles = PAGE_ROLE_PERMISSIONS[pageId];
  if (!permittedRoles) return true; // Default allow if page is unspecified
  return permittedRoles.includes(role as UserRole);
}

/**
 * Checks if user is an Executive (Director) by role or known email pattern.
 */
export function isExecutiveAccount(profile?: UserProfile | null, userEmail?: string | null): boolean {
  if (!profile && !userEmail) return false;
  if (profile?.role === 'admin' || profile?.role === 'executive') return true;

  const email = (profile?.email || userEmail || '').toLowerCase();
  return email.includes('soly') || email.includes('bhalla');
}
